import React, {
  FocusEvent,
  InputHTMLAttributes,
  KeyboardEvent,
  RefObject,
  useEffect,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  TextFieldProps,
} from '@mui/material/TextField';
import dayjs from 'utils/dayjs';
import {
  formatTwoDigitDate,
} from 'utils/date';
import MaskedTextField from 'core/components/MaskedTextField';

const mask = [{
  mask: 'MM{/}DD{/}YYYY',
  blocks: {
    MM: {
      mask: '00',
      definitions: {
        0: /[0-9]/,
      },
      placeholderChar: 'm',
    },
    DD: {
      mask: '00',
      definitions: {
        0: /[0-9]/,
      },
      placeholderChar: 'd',
    },
    YYYY: {
      mask: '0000',
      definitions: {
        0: /[0-9]/,
      },
      placeholderChar: 'y',
    },
  },
  lazy: false,
  overwrite: true,
  eager: true,
}];

type DateValue = string | Date | null | undefined;

type Props = {
  id?: string;
  value?: unknown;
  onChange: (value: DateValue) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  defaultValue?: unknown;
  disabled?: boolean;
  placeholder?: string;
  slotProps?: TextFieldProps['slotProps'];
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
};

export default function DateTextField({
  value: committedValueProp,
  className,
  onChange,
  onKeyDown,
  defaultValue = '',
  disabled = false,
  placeholder = 'mm/dd/yyyy',
  inputProps,
  ...props
}: Props) {
  const normalizeValue = (value: unknown): DateValue => (
    value instanceof Date || typeof value === 'string'
      ? value
      : undefined
  );

  const committedValue = useMemo(
    () => normalizeValue(committedValueProp),
    [committedValueProp],
  );
  const [value, setValue] = useState<DateValue>(committedValue);

  const formatValue = (nextValue: unknown): string | undefined => {
    if (nextValue instanceof Date) {
      return formatTwoDigitDate(nextValue);
    }

    if (typeof nextValue === 'string') {
      return nextValue;
    }

    if (isNil(nextValue)) {
      return undefined;
    }

    return String(nextValue);
  };

  useEffect(() => {
    const nextValue = formatValue(committedValue);
    const currentValue = formatValue(value);

    if (nextValue !== currentValue) {
      setValue(committedValue);
    }
  }, [committedValue]);

  return (
    <MaskedTextField
      {...props}
      mask={mask}
      variant="outlined"
      value={formatValue(value)}
      defaultValue={formatValue(defaultValue)}
      inputRef={props.inputRef}
      onAccept={setValue}
      slotProps={{
        ...props.slotProps,
        htmlInput: {
          ...props.slotProps?.htmlInput,
          ...inputProps,
        },
      }}
      disabled={disabled}
      onComplete={(nextValue: string) => {
        if (dayjs(nextValue).isValid()) {
          onChange(nextValue);
        }
      }}
      onBlur={(event: FocusEvent<HTMLInputElement>) => {
        if (!dayjs(value).isValid()) {
          setValue(committedValue);
        }

        props.onBlur?.(event);
      }}
      onKeyDown={onKeyDown}
      className={clsx('populate-input populate-date-input text-xs', className)}
    />
  );
}
